import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The course a course-scoped page is pointed at, and the term it sits in.
 *
 * Course is session context, the same as the academic year: the gradebook used
 * to carry a 256px rail whose whole job was answering "which class am I looking
 * at", which is a question about the session rather than about the page. So the
 * answer moved to the topbar beside the year, and the rail went.
 *
 * Held as ids rather than objects, for the same reason the year is: an id
 * survives a reload without going stale. Every id here is resolved against
 * freshly-fetched lists on each render (`useCourseScope`), so one belonging to
 * another school, another year, or a course since deleted falls back rather
 * than showing a gradebook that is not there.
 */
interface CourseScopeState {
  /** The term the user last chose, or null to follow the year's first term. */
  selectedTermId: string | null;
  setSelectedTermId: (id: string | null) => void;
  /** The course the user last chose, or null to follow the term's first. */
  selectedCourseId: string | null;
  setSelectedCourseId: (id: string | null) => void;

  /**
   * How many pages on screen are scoped to a course.
   *
   * The pills are page context living in shared chrome, so the topbar has to
   * know whether anything on screen is course-scoped — a Course pill above the
   * student roster would be naming a scope that page does not have.
   *
   * A count rather than a flag: React mounts effects twice under StrictMode,
   * and a flag would be switched off by the first unmount of a page that is
   * still on screen. Deliberately not persisted — it describes what is
   * rendered, not what the user chose.
   */
  scopeCount: number;
  acquireScope: () => void;
  releaseScope: () => void;
}

export const useCourseScopeStore = create<CourseScopeState>()(
  persist(
    (set) => ({
      selectedTermId: null,
      setSelectedTermId: (selectedTermId) => set({ selectedTermId }),
      selectedCourseId: null,
      setSelectedCourseId: (selectedCourseId) => set({ selectedCourseId }),
      scopeCount: 0,
      acquireScope: () => set((state) => ({ scopeCount: state.scopeCount + 1 })),
      releaseScope: () =>
        set((state) => ({ scopeCount: Math.max(0, state.scopeCount - 1) })),
    }),
    {
      name: 'course-scope-storage',
      partialize: ({ selectedTermId, selectedCourseId }) => ({
        selectedTermId,
        selectedCourseId,
      }),
    }
  )
);
